import { useMemo, useState, type ReactNode } from 'react';
import { Printer, RotateCcw } from 'lucide-react';
import { Button } from '../ui/Button';
import { Segments } from '../ui/ListToolbar';
import { DossiersByStageChart } from './charts/DossiersByStageChart';
import { DossiersByTypeChart } from './charts/DossiersByTypeChart';
import { RevenueMonthlyChart } from './charts/RevenueMonthlyChart';
import { HoursByLawyerChart } from './charts/HoursByLawyerChart';
import { ProfitabilityTable } from './ProfitabilityTable';
import { CsvExportButton } from './CsvExportButton';
import { WidgetGrid } from '../dashboard/WidgetGrid';
import { WidgetDragHandle } from '../dashboard/WidgetCardHeader';
import { resolveDashboardLayout } from '../dashboard/dashboardWidgets';
import { useAuthStore } from '../../store/useAuthStore';
import { useStatsLayoutStore } from '../../store/useStatsLayoutStore';
import {
  useStatsDossiersByStage,
  useStatsDossiersByType,
  useStatsRevenueMonthly,
  useStatsHoursByLawyer,
  useStatsDossierProfitability,
} from '../../hooks/queries/useStats';
import {
  buildDossiersByStageCsv,
  buildDossiersByTypeCsv,
  buildRevenueMonthlyCsv,
  buildHoursByLawyerCsv,
  buildProfitabilityCsv,
} from '../../services/stats/statsCsvBuilders';

type Period = 3 | 6 | 12;

/** Blocs de la page, avec leur place par défaut dans la grille (12 colonnes, rangées de 40 px). */
const STATS_WIDGETS = [
  { key: 'stage', defaultSpan: 6, minSpan: 4, defaultRows: 5, minRows: 4 },
  { key: 'type', defaultSpan: 6, minSpan: 4, defaultRows: 5, minRows: 4 },
  { key: 'revenue', defaultSpan: 6, minSpan: 4, defaultRows: 6, minRows: 4 },
  { key: 'hours', defaultSpan: 6, minSpan: 4, defaultRows: 6, minRows: 4 },
  { key: 'profitability', defaultSpan: 12, minSpan: 6, defaultRows: 10, minRows: 5 },
] as const;

function todayStamp(): string {
  return new Date().toISOString().slice(0, 10);
}

export function StatsPage() {
  const [period, setPeriod] = useState<Period>(12);
  const collaboratorId = useAuthStore((s) => s.collaborator?.id);
  const storedBoxes = useStatsLayoutStore((s) =>
    collaboratorId ? s.boxesByCollaborator[collaboratorId] : undefined,
  );
  const setBoxes = useStatsLayoutStore((s) => s.setBoxes);
  const resetLayout = useStatsLayoutStore((s) => s.reset);
  const boxes = useMemo(
    () => resolveDashboardLayout([...STATS_WIDGETS], storedBoxes),
    [storedBoxes],
  );

  // Datasets pour les exports — les hooks sont déjà appelés par les enfants ;
  // React Query déduplique automatiquement, donc on ne paie pas la requête deux fois.
  const byStage = useStatsDossiersByStage();
  const byType = useStatsDossiersByType();
  const revenue = useStatsRevenueMonthly();
  const hours = useStatsHoursByLawyer();
  const profitability = useStatsDossierProfitability();

  const stamp = todayStamp();

  const csvStage = useMemo(
    () => buildDossiersByStageCsv(byStage.data ?? []),
    [byStage.data],
  );
  const csvType = useMemo(
    () => buildDossiersByTypeCsv(byType.data ?? []),
    [byType.data],
  );
  const csvRevenue = useMemo(() => {
    const sliced = (revenue.data ?? []).slice(-period);
    return buildRevenueMonthlyCsv(sliced);
  }, [revenue.data, period]);
  const csvHours = useMemo(
    () => buildHoursByLawyerCsv(hours.data ?? []),
    [hours.data],
  );
  const csvProfitability = useMemo(
    () => buildProfitabilityCsv(profitability.data ?? []),
    [profitability.data],
  );

  return (
    <div className="main-container-gdm space-y-4 print:space-y-3">
      <div className="flex items-center justify-end print:hidden">
        <div className="flex items-center gap-2">
          <Segments
            label="Période"
            value={String(period) as '3' | '6' | '12'}
            onChange={(v) => setPeriod(Number(v) as 3 | 6 | 12)}
            options={[
              { value: '3', label: '3 mois' },
              { value: '6', label: '6 mois' },
              { value: '12', label: '12 mois' },
            ]}
          />
          {storedBoxes && collaboratorId ? (
            <Button variant="ghost" onClick={() => resetLayout(collaboratorId)}>
              <RotateCcw className="h-3.5 w-3.5" />
              Disposition par défaut
            </Button>
          ) : null}
          <Button variant="secondary" onClick={() => window.print()}>
            <Printer className="h-3.5 w-3.5" />
            Imprimer / PDF
          </Button>
        </div>
      </div>

      {/* Titre visible uniquement à l'impression */}
      <div className="hidden print:block">
        <h1 className="text-lg font-semibold mb-1">Statistiques du cabinet</h1>
        <p className="text-xs text-neutral-600">
          Imprimé le {new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
          {' · '}CA mensuel sur {period} mois
        </p>
      </div>

      <WidgetGrid
        items={[...STATS_WIDGETS]}
        boxes={boxes}
        onCommit={(next) => {
          if (collaboratorId) setBoxes(collaboratorId, next);
        }}
        render={(key) => {
          switch (key) {
            case 'stage':
              return (
                <Card
                  title="Dossiers par étape"
                  action={
                    <CsvExportButton
                      filename={`stats-dossiers-par-etape_${stamp}`}
                      content={csvStage}
                      disabled={!byStage.data || byStage.data.length === 0}
                    />
                  }
                >
                  <DossiersByStageChart />
                </Card>
              );
            case 'type':
              return (
                <Card
                  title="Dossiers par type d'affaire"
                  action={
                    <CsvExportButton
                      filename={`stats-dossiers-par-type_${stamp}`}
                      content={csvType}
                      disabled={!byType.data || byType.data.length === 0}
                    />
                  }
                >
                  <DossiersByTypeChart />
                </Card>
              );
            case 'revenue':
              return (
                <Card
                  title={`CA HT mensuel (${period} mois)`}
                  action={
                    <CsvExportButton
                      filename={`stats-ca-mensuel_${period}m_${stamp}`}
                      content={csvRevenue}
                      disabled={!revenue.data || revenue.data.length === 0}
                    />
                  }
                >
                  <RevenueMonthlyChart months={period} />
                </Card>
              );
            case 'hours':
              return (
                <Card
                  title="Heures par avocat (12 mois)"
                  action={
                    <CsvExportButton
                      filename={`stats-heures-par-avocat_${stamp}`}
                      content={csvHours}
                      disabled={!hours.data || hours.data.length === 0}
                    />
                  }
                >
                  <HoursByLawyerChart />
                </Card>
              );
            case 'profitability':
              return (
                <Card
                  title="Rentabilité par dossier"
                  action={
                    <CsvExportButton
                      filename={`stats-rentabilite-dossiers_${stamp}`}
                      content={csvProfitability}
                      disabled={!profitability.data || profitability.data.length === 0}
                    />
                  }
                >
                  <ProfitabilityTable />
                </Card>
              );
            default:
              return null;
          }
        }}
      />
    </div>
  );
}

function Card({
  title,
  children,
  action,
}: {
  title: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col rounded-xl border border-neutral-200 bg-white px-[18px] py-4 print:break-inside-avoid">
      <div className="flex items-center gap-2 mb-3 shrink-0">
        <span className="-ml-2 empty:hidden">
          <WidgetDragHandle />
        </span>
        <h2 className="flex-1 min-w-0 truncate text-sm font-semibold text-cime-text-primary">{title}</h2>
        {action}
      </div>
      <div className="min-h-0 flex-1">{children}</div>
    </div>
  );
}
